import { useEffect, useRef, useState } from "react";
import { Save, Loader2, MapPin, Briefcase, Mail, ShieldCheck, Camera, AlertTriangle, Ban } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { fileUrl, uploadImage } from "../lib/api";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ name: "", location: "", experience_years: "", bio: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        location: user.location || "",
        experience_years: user.experience_years ?? "",
        bio: user.bio || "",
      });
    }
  }, [user]);

  if (!user) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await updateProfile({
      name: form.name.trim(),
      location: form.location.trim() || null,
      experience_years: form.experience_years === "" ? null : parseInt(form.experience_years, 10),
      bio: form.bio.trim() || null,
    });
    setBusy(false);
    if (r.ok) toast.success("Profil aktualisiert");
    else toast.error(r.error);
  };

  const onPickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Bitte ein Bild auswählen.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Bild zu groß (max. 5 MB).");
      return;
    }
    setUploading(true);
    try {
      const up = await uploadImage(f);
      const r = await updateProfile({ avatar_url: up.url });
      if (r.ok) toast.success("Profilbild aktualisiert");
      else toast.error(r.error);
    } catch (err) {
      console.error("avatar upload failed", err);
      toast.error("Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="profile-page" className="space-y-4 sm:space-y-6">
      <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">Mein Bereich</p>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter -mt-2 sm:-mt-4">
        Profil
      </h1>

      {/* Bento header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 bg-[#FFFFFF] border border-slate-200 rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background:
                "radial-gradient(600px circle at 0% 0%, rgba(0,102,255,0.35), transparent 60%)",
            }}
          />
          <div className="relative flex items-center gap-5">
            <div className="relative">
              <img
                src={fileUrl(user.avatar_url)}
                alt={user.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-[#1D5BFF]/40 shadow-[0_0_28px_rgba(0,102,255,0.4)]"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="upload-avatar-btn"
                className="absolute -bottom-1 -right-1 bg-[#1D5BFF] hover:bg-[#1546C9] text-white rounded-full p-2 shadow-lg transition-transform active:scale-90"
                title="Profilbild ändern"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="avatar-input" />
            </div>
            <div>
              <div className="font-display text-2xl font-black text-slate-900">{user.name}</div>
              <div className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                <Mail className="w-3.5 h-3.5" /> {user.email}
              </div>
              <div className="text-xs uppercase tracking-wider text-[#4DBFFF] mt-2 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                {user.role === "admin" ? "Administrator" : user.is_approved ? "Verifizierter Fahrer" : "Wartet auf Freischaltung"}
              </div>
            </div>
          </div>
        </div>
        <Stat icon={Briefcase} label="Erfahrung" value={user.experience_years != null ? `${user.experience_years} J.` : "–"} />
        <Stat icon={MapPin} label="Standort" value={user.location || "–"} />
      </div>

      {/* Warning + ban status */}
      {(user.warnings > 0 || user.banned) && (
        <div
          className={`rounded-2xl p-5 border ${user.banned ? "bg-red-500/10 border-red-500/40" : "bg-amber-500/10 border-amber-500/30"}`}
          data-testid="warnings-status"
        >
          <div className="flex items-center gap-3">
            {user.banned ? (
              <Ban className="w-5 h-5 text-red-400" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            )}
            <div>
              <div className="font-display font-bold text-slate-900">
                {user.banned ? "Konto gesperrt" : `${user.warnings}/3 Verwarnungen`}
              </div>
              <div className="text-sm text-slate-700">
                {user.banned
                  ? "Bitte kontaktiere den Administrator zur Klärung."
                  : `Noch ${3 - user.warnings} Verwarnung(en) bis zur automatischen Sperre.`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit form */}
      <form onSubmit={save} className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-display text-xl font-bold text-slate-900">Profil bearbeiten</h2>

        <Field label="Name" required value={form.name} onChange={set("name")} testId="profile-name" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Standort" value={form.location} onChange={set("location")} testId="profile-location" />
          <Field label="Erfahrung (Jahre)" type="number" min="0" max="80" value={form.experience_years} onChange={set("experience_years")} testId="profile-experience" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
            Über mich
          </label>
          <textarea
            value={form.bio}
            onChange={set("bio")}
            rows={4}
            placeholder="Erzähle kurz, welche Strecken du fährst, welche Erfahrung du hast…"
            data-testid="profile-bio-input"
            className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/30"
          />
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="profile-save-btn"
          className="bg-[#1D5BFF] hover:bg-[#1546C9] disabled:opacity-60 text-slate-900 font-bold px-6 py-3 rounded-full transition-all active:scale-95 shadow-[0_8px_30px_rgba(0,102,255,0.35)] flex items-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Speichern
        </button>
      </form>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-5 flex flex-col justify-between">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
        <Icon className="w-3.5 h-3.5 text-[#4DBFFF]" /> {label}
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-2">{value}</div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
        {label}
      </label>
      <input
        {...props}
        data-testid={`${testId}-input`}
        className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/30"
      />
    </div>
  );
}
